/**
 * Collection Tabs - Handles tab filtering and pagination
 */

class CollectionTabs {
  constructor() {
    this.container = document.querySelector(".custom-collection-tabbed");
    if (!this.container) {
      return;
    }

    this.tabButtons = this.container.querySelectorAll(".tab-button");
    this.tabSelect = this.container.querySelector(".tab-select");
    this.productItems = this.container.querySelectorAll(".product-item");
    this.productsGrid = this.container.querySelector(
      ".collection-products-grid",
    );
    this.paginationPrev = this.container.querySelector(".pagination-prev");
    this.paginationNext = this.container.querySelector(".pagination-next");
    this.currentPageEl = this.container.querySelector(".current-page");
    this.totalPagesEl = this.container.querySelector(".total-pages");
    this.rangeStartEl = this.container.querySelector(".range-start");
    this.rangeEndEl = this.container.querySelector(".range-end");
    this.rangeTotalEl = this.container.querySelector(".range-total");

    this.defaultFilter = this.container.dataset.defaultFilter || "all";
    this.activeFilter = this.defaultFilter;
    this.currentPage = 1;
    this.productsPerPage =
      parseInt(this.productsGrid?.dataset?.productsPerPage) || 9;
    this.filteredProducts = [];

    this.init();
  }

  normalizeTag(str) {
    // Normalize for resilient tab matching (tab label/tag_filter formatting vs Shopify tag formatting).
    // We aim to compare "concepts" rather than exact punctuation.
    // Examples handled: spaces, underscores, slashes, ampersands, quotes, repeated dashes.
    return String(str || "")
      .toLowerCase()
      .trim()
      .replace(/['"]/g, "") // remove quotes
      .replace(/&/g, "and")
      .replace(/[_\/]+/g, "-") // underscores + slashes -> hyphens
      .replace(/[^a-z0-9]+/g, "-") // everything else -> hyphen
      .replace(/-+/g, "-") // collapse repeated hyphens
      .replace(/^-|-$/g, ""); // trim leading/trailing hyphens
  }

  init() {
    // Set up tab click handlers
    this.tabButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleTabClick(e);
      });
    });

    if (this.tabSelect) {
      this.tabSelect.addEventListener("change", (e) => {
        const filter = e.target.value;
        this.setActiveFilterUI(filter);
        this.filterProducts(filter);
      });
    }

    // Set up pagination handlers
    if (this.paginationPrev) {
      this.paginationPrev.addEventListener("click", () =>
        this.goToPreviousPage(),
      );
    }
    if (this.paginationNext) {
      this.paginationNext.addEventListener("click", () => this.goToNextPage());
    }

    // Initial filter
    this.setActiveFilterUI(this.defaultFilter);
    this.filterProducts(this.defaultFilter, { shouldScroll: false });
  }

  handleTabClick(e) {
    const button = e.currentTarget;
    const filter = button.dataset.filter;

    // Update active state
    this.setActiveFilterUI(filter);

    // Filter products
    this.filterProducts(filter);
  }

  setActiveFilterUI(filter) {
    this.tabButtons.forEach((btn) => btn.classList.remove("active"));
    const activeButton = Array.from(this.tabButtons).find(
      (btn) => btn.dataset.filter === filter,
    );
    if (activeButton) {
      activeButton.classList.add("active");
    }

    if (this.tabSelect && this.tabSelect.value !== filter) {
      // Only set dropdown value if the option exists.
      const hasOption = Array.from(this.tabSelect.options).some(
        (opt) => String(opt.value) === String(filter),
      );
      if (hasOption) this.tabSelect.value = filter;
    }
  }

  filterProducts(filter, { shouldScroll = true } = {}) {
    this.activeFilter = filter;
    this.currentPage = 1;

    // Get filtered products
    const normalizedFilter = this.normalizeTag(filter);
    const rawFilter = String(filter || "").toLowerCase().trim();

    this.filteredProducts = Array.from(this.productItems).filter((item) => {
      if (filter === "all") return true;

      const tagsString = item.dataset.tags || "";
      if (!tagsString) return false;

      const rawTags = tagsString
        .split(",")
        .map((tag) => String(tag || "").toLowerCase().trim())
        .filter(Boolean);

      const normalizedTags = rawTags.map((tag) => this.normalizeTag(tag));

      // Match either by normalized form OR exact lowercased tag name.
      const matchNormalized = normalizedTags.includes(normalizedFilter);
      const matchRaw = rawTags.includes(rawFilter);
      return matchNormalized || matchRaw;
    });

    // Update display
    this.updateProductDisplay();
    this.updatePagination();

    // Scroll to top of collection
    if (shouldScroll && this.container) {
      this.container.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  updateProductDisplay() {
    const startIndex = (this.currentPage - 1) * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;

    // Hide all products first
    this.productItems.forEach((item) => {
      item.classList.add("hidden");
    });

    // Show filtered products for current page
    this.filteredProducts.forEach((item, index) => {
      if (index >= startIndex && index < endIndex) {
        item.classList.remove("hidden");
      }
    });

    // Add fade-in animation
    setTimeout(() => {
      this.filteredProducts.forEach((item, index) => {
        if (index >= startIndex && index < endIndex) {
          item.style.animation = "fadeIn 0.3s ease forwards";
        }
      });
    }, 10);
  }

  updatePagination() {
    const totalPages = Math.ceil(
      this.filteredProducts.length / this.productsPerPage,
    );
    const totalItems = this.filteredProducts.length;

    // Update counter
    if (this.currentPageEl) this.currentPageEl.textContent = this.currentPage;
    if (this.totalPagesEl) this.totalPagesEl.textContent = totalPages || 1;

    // Update range counter (X-Y of Z)
    const startItem =
      totalItems > 0 ? (this.currentPage - 1) * this.productsPerPage + 1 : 0;
    const endItem =
      totalItems > 0
        ? Math.min(this.currentPage * this.productsPerPage, totalItems)
        : 0;
    if (this.rangeStartEl) this.rangeStartEl.textContent = startItem;
    if (this.rangeEndEl) this.rangeEndEl.textContent = endItem;
    if (this.rangeTotalEl) this.rangeTotalEl.textContent = totalItems;

    // Update button states
    if (this.paginationPrev) {
      this.paginationPrev.disabled = this.currentPage === 1;
    }

    if (this.paginationNext) {
      this.paginationNext.disabled = this.currentPage >= totalPages;
    }

    // Hide pagination if only one page
    const paginationControls = this.container.querySelectorAll(
      ".pagination-controls",
    );
    if (paginationControls && paginationControls.length) {
      const shouldHide = totalPages <= 1;
      paginationControls.forEach((el) => {
        el.style.display = shouldHide ? "none" : "flex";
      });
    }
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updateProductDisplay();
      this.updatePagination();
      this.container.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  goToNextPage() {
    const totalPages = Math.ceil(
      this.filteredProducts.length / this.productsPerPage,
    );
    if (this.currentPage < totalPages) {
      this.currentPage++;
      this.updateProductDisplay();
      this.updatePagination();
      this.container.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
}

// Add CSS animation for fade-in
function addFadeInAnimation() {
  if (!document.getElementById("collection-tabs-animation")) {
    const style = document.createElement("style");
    style.id = "collection-tabs-animation";
    style.textContent = `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
  }
}

// Initialize function
function initCollectionTabs() {
  console.log("Initializing collection tabs...");
  addFadeInAnimation();
  new CollectionTabs();
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCollectionTabs);
} else {
  initCollectionTabs();
}

// Re-initialize on section re-render (Shopify theme editor)
document.addEventListener("shopify:section:load", (event) => {
  if (
    event.detail?.sectionId &&
    document.querySelector(".custom-collection-tabbed")
  ) {
    initCollectionTabs();
  }
});
